import React, { forwardRef } from 'react'

import { useTextArea } from './hooks'
import * as S from './styles'

import { FormItem } from '../FormItem'

import type { TextAreaProps } from './types'

/**
 * Form text area component
 * @component
 * @param {object} error - error object {type: 'required', message: 'field is required'}
 * @param {string} label - text label above input
 * @param {string} placeholder - placeholder text
 * @param {function} onBlur - onBlur handler
 * @param {boolean} hidden - hide input dom element (display:none)
 * @param {boolean} shouldExpandWithText - if true, height will be dynamic depending on text (and impedes manual resize)
 * @example
 * import { useForm } from 'react-hook-form'
 * const { register, formState: { errors } } = useForm()
 *
 * return (
 *   <TextArea {...register('name', { required: 'this field is required' })}
 *              label='Name'
 *              error={errors.name}
 *              placeholder="Type your name..."
 *   />
 * )
 */

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      error,
      label,
      hidden,
      hint,
      shouldKeepLabelSpacing,
      shouldExpandWithText,
      onChange,
      ...restProps
    },
    ref
  ) => {
    const { scrollHeight, onChangeText, handleRef } = useTextArea({
      shouldExpandWithText,
      onChange,
      ref,
    })

    return (
      <FormItem
        shouldKeepLabelSpacing={shouldKeepLabelSpacing}
        label={label}
        error={error}
        hidden={hidden}
        hint={hint}
      >
        <S.TextAreaWrapper>
          <S.TextArea
            onChange={onChangeText}
            ref={handleRef}
            $error={error}
            $canBeManuallyResized={!shouldExpandWithText}
            $shouldExpandWithText={shouldExpandWithText}
            $scrollHeight={scrollHeight}
            {...restProps}
          />
        </S.TextAreaWrapper>
      </FormItem>
    )
  }
)

TextArea.displayName = 'TextArea'
